import { TFetchError } from '@/api/api-error.types';
import { getApiErrorMessage } from '@/utils/api-error-message';

/**
 * Domain error codes the role-administration endpoints return in the standard
 * `{ code, details }` 4xx body — the actual namespaced wire values from
 * `EntityErrorCodes` (`libs/common/src/enums/entity-error-codes.enum.ts`), not
 * the bare identifiers the FE mock contract sketched. Anything not listed here
 * (e.g. `ENTITY.ROLE.NOT_FOUND`, `ENTITY.ROLE.NAME_CONFLICT`) falls back to the
 * generic message — those aren't in the frontend's explicit-handling list.
 */
export const ROLE_ADMIN_ERROR_CODES = {
  /** Caller tried to grant a permission they do not hold themselves. */
  permissionNotHeldByGrantor: 'ENTITY.ROLE.PERMISSION_NOT_HELD_BY_GRANTOR',
  /** Caller tried to add or remove a `base` row on a system role. */
  roleBaseImmutable: 'ENTITY.ROLE.BASE_IMMUTABLE',
  /** Caller tried to delete a custom role that still has members. */
  roleHasMembers: 'ENTITY.ROLE.HAS_MEMBERS',
  /** Caller tried to reset a custom role — only system roles have a default. */
  roleHasNoDefault: 'ENTITY.ROLE.HAS_NO_DEFAULT',
  /** The removal would leave the organisation without a role administrator. */
  lastRoleAdminProtected: 'ENTITY.ROLE.LAST_ADMIN_PROTECTED',
  /** The permission is not grantable on this plane (e.g. a Gamma-only permission). */
  permissionNotInPalette: 'ENTITY.ROLE.PERMISSION_NOT_IN_PALETTE',
  /** Any attempt to edit, duplicate, delete or reset the Owner role, or to remove
   * it from someone other than that member themself. */
  ownerProtected: 'ENTITY.ROLE.OWNER_PROTECTED',
  /** Caller lacks `EntityManageRoles`; the scope write is gated like the role itself. */
  scopeForbidden: 'ENTITY.ROLE.SCOPE_FORBIDDEN',
  /** Owner always holds full view, so it cannot be limited to selected categories. */
  roleNotScopable: 'ENTITY.ROLE.NOT_SCOPABLE',
  /** A roles admin without full view added or removed a category they cannot open. */
  scopeResourceLocked: 'ENTITY.ROLE.SCOPE_RESOURCE_LOCKED',
} as const;

export type RoleAdminErrorCode =
  (typeof ROLE_ADMIN_ERROR_CODES)[keyof typeof ROLE_ADMIN_ERROR_CODES];

const MESSAGE_BY_CODE: Record<RoleAdminErrorCode, string> = {
  [ROLE_ADMIN_ERROR_CODES.permissionNotHeldByGrantor]:
    'You can only grant permissions you hold yourself.',
  [ROLE_ADMIN_ERROR_CODES.roleBaseImmutable]:
    "This is a built-in permission of a default role and can't be changed. Duplicate the role to customise it.",
  [ROLE_ADMIN_ERROR_CODES.roleHasMembers]:
    'This role still has members. Remove them before deleting it.',
  [ROLE_ADMIN_ERROR_CODES.roleHasNoDefault]:
    'This role has no default to reset — only default (system) roles have one.',
  [ROLE_ADMIN_ERROR_CODES.lastRoleAdminProtected]:
    'Your organisation must keep at least one role administrator.',
  [ROLE_ADMIN_ERROR_CODES.permissionNotInPalette]:
    "This permission can't be granted from an entity role.",
  [ROLE_ADMIN_ERROR_CODES.ownerProtected]:
    'Only an existing Owner can change the Owner role, and only that member can remove it from themself.',
  [ROLE_ADMIN_ERROR_CODES.scopeForbidden]:
    "You don't have permission to change which categories this role opens.",
  [ROLE_ADMIN_ERROR_CODES.roleNotScopable]:
    "The Owner role always sees every category and can't be limited.",
  [ROLE_ADMIN_ERROR_CODES.scopeResourceLocked]:
    'You can only add or remove categories you can open.',
};

const isRoleAdminErrorCode = (code: string): code is RoleAdminErrorCode => code in MESSAGE_BY_CODE;

/**
 * Turns a failed role-administration request into the message to show.
 *
 * These endpoints opt out of the global error toast (see `shouldSuppressDefaultErrorToast`
 * in `src/api/index.ts`) precisely so this — not the generic handler — decides what the
 * caller sees.
 *
 * The backend's own `details` always wins (per the FE contract: "The `details` field
 * carries a human-readable sentence — surface it directly"), via `getApiErrorMessage`'s
 * existing `data.details` → `validationErrors` → `message` priority. `MESSAGE_BY_CODE` is
 * only the last resort: it is handed to `getApiErrorMessage` as the *fallback* value, so it
 * is only ever shown when the response body carries no text of its own for one of the seven
 * documented codes.
 */
export const getRoleAdminErrorMessage = (error: unknown, fallback = 'Something went wrong') => {
  const fetchError = error as TFetchError;
  const code = fetchError?.data?.code;
  const codeFallback = code && isRoleAdminErrorCode(code) ? MESSAGE_BY_CODE[code] : fallback;

  return getApiErrorMessage(fetchError, codeFallback);
};
